/**
 * Landing Page
 *
 * Main marketing landing page for Tangi.
 * Instrumented with funnel analytics for conversion tracking.
 */
import { useEffect, useRef } from 'react';

import { FUNNEL_EVENTS } from '@/shared/analytics';
import { useAnalytics } from '@/shared/hooks';
import { getExperimentAssignment } from '@/shared/experiments';
import { getOrCreateSessionId } from '@/shared/utils/session';
import { authService } from '@/features/auth';
import { ErrorBoundary } from '@/shared/components';

import {
  CreationSection,
  HeroSection,
  LandingNav,
  PhilosophySection,
  TestimonialsSection,
  TransformSection,
} from './components';
import { FullPageLayout, Stack } from "@/ui";

/**
 * Extract UTM parameters from URL search params.
 * Only returns keys with defined values to avoid Mixpanel warnings.
 */
function getUtmParams(): Record<string, string> {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  const utm: Record<string, string> = {};

  const keys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  keys.forEach(key => {
    const value = params.get(key);
    if (value) {
      utm[key] = value;
    }
  });

  return utm;
}

export const LandingPage = (): React.JSX.Element => {
  const { trackFunnel } = useAnalytics();
  const hasTrackedRef = useRef(false);
  const experimentAssignmentRef = useRef<{ variant: string; experimentId: string } | null>(null);

  // Get experiment variant assignment (deterministic per user/session)
  useEffect(() => {
    if (experimentAssignmentRef.current) return; // Already assigned

    const getUserId = async (): Promise<string | undefined> => {
      try {
        const isAuth = await authService.isAuthenticated();
        if (isAuth) {
          const user = authService.getCurrentUser();
          if (user && 'uid' in user && typeof user.uid === 'string') {
            return user.uid;
          }
        }
      } catch {
        // Ignore auth errors
      }
      return undefined;
    };

    const assignExperiment = async (): Promise<void> => {
      const userId = await getUserId();
      const sessionId = getOrCreateSessionId();

      // Get experiment assignment for hero CTA experiment
      const assignment = getExperimentAssignment(
        'landing_hero_cta',
        userId,
        sessionId
      );

      experimentAssignmentRef.current = {
        variant: assignment.variant,
        experimentId: assignment.experimentId,
      };
    };

    void assignExperiment();
  }, []);

  // Track landing page view on mount (once only)
  useEffect(() => {
    if (hasTrackedRef.current) return;
    hasTrackedRef.current = true;

    const utmParams = getUtmParams();

    const referrer = document.referrer || '';
    trackFunnel(FUNNEL_EVENTS.LANDING_VIEWED, {
      ...(referrer ? { referrer } : {}),
      ...utmParams,
      // Include experiment variant in landing view event
      ...(experimentAssignmentRef.current && {
        experiment_id: experimentAssignmentRef.current.experimentId,
        experiment_variant: experimentAssignmentRef.current.variant,
      }),
    });
  }, [trackFunnel]);

  return (
    <ErrorBoundary>
      <FullPageLayout background="default">
        <LandingNav />
        <HeroSection />

        {/* Scroll-triggered sections */}
        <Stack gap={0}>
          <PhilosophySection />
          <TransformSection />
          <CreationSection />
          <TestimonialsSection />
        </Stack>
      </FullPageLayout>
    </ErrorBoundary>
  );
};
